import React, { useContext, useEffect, useState } from 'react'
import useMemberTable from '../../context/MemberTableContext'
import { modalConfirmationMessageId, modalMembersTblBtnId, UserBadge } from '../Utils'
import Icons from '../../../../../common/icons/Icons'
import Profile from '../../../../../common/profile/Profile'
import ResponseMessage from "../../../../../common/modal/ResponseMessage"
import { formRequest } from '../../form/Utils'
import { getPaletteColor } from '../../../MenuTabs/Utils'
import MembersTableButton from '../MembersTableButton'
import { TeamContext } from '../../../MenuTabs/MenuTabs'
import { createColumnHelper } from '@tanstack/react-table'
import MembersyncTable from '../../../../../../orgs/MembersyncTable'
import MicroModal from 'micromodal'

const UserInfo = ({ modalId }) => {

  const { selectedUser, refetchMembers } = useMemberTable()
  const [ isEditing, setIsEditing ] = useState(false)
  const [ fullName, setFullName ] = useState({
    firstName: '',
    lastName: ''
  })
  const [ responseMessage, setResponseMessage ] = useState('')
  const [ response, setResponse ] = useState(null)
  const { teamData } = useContext(TeamContext)

  useEffect(() => {
    if(selectedUser){
      setFullName( prevState => ({ 
        ...prevState,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName
      }))
    }

  }, [ selectedUser ])

  const formData = new FormData()
  formData.append('action', 'msync_update_user_full_name')
  formData.append('team_id', selectedUser.teamId)
  formData.append('team_type', selectedUser.teamType)
  formData.append('user_id', selectedUser.userId)
  formData.append('first_name', fullName.firstName)
  formData.append('last_name', fullName.lastName)
  formData.append('nonce', group_dashboard.nonce_members)

  const { isLoading, isFetching, isError, error, refetch, data } = formRequest( formData, setResponseMessage, refetchMembers )

  const handleUpdate = () => {
    setResponse(null)
    setResponseMessage('')
    if( isEditing
      && selectedUser 
      && fullName.firstName === selectedUser.firstName
      && fullName.lastName === selectedUser.lastName ){
        setIsEditing(false)
    }else{
      refetch()
    }
  }

  const handleEditInfo = () => {
    setResponse(null)
    setResponseMessage('')
    setIsEditing(true)
  }

  useEffect(() => {
    if( isError ){
      setResponse({ type: 'error', message: error.message })
    }else if( responseMessage ){
      setResponse({ type: 'error', message: responseMessage })
    }
  }, [ isError, responseMessage ])

  async function removeLicenseMember( licenseID, memberID ) {
    const URLParams = new URLSearchParams(location.search)
    const orgID = URLParams.get('org')

    const currentRestUrl = window.group_dashboard.base
    const url = currentRestUrl + 'membersync/licensing/v1/org/' + selectedUser.teamId + '/holder/' + memberID + '/license/' + licenseID
    const nonce = window.group_dashboard.nonce

    let licenseRemoved = false

    const res = await fetch(url, {
        method: 'DELETE',
        headers:{
            'X-WP-Nonce': nonce,
            'Content-type': 'application/json'
        }
    })
    refetch()
  }

  async function assignLicenses( licenses, userId, licensePresets, licenseExpiresDate ) {
    const currentRestUrl = window.group_dashboard.base
    const url = currentRestUrl + 'membersync/licensing/v1/org/' + selectedUser.teamId + '/member/assign'
    const nonce = window.group_dashboard.nonce

    let memberEdited = false
    let error = []
    let newLicense = []

    await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
            'member_id': userId,
            'licenses': licenses,
            'license_presets': licensePresets,
            'license_expires_date': licenseExpiresDate
        }),
        headers:{
            'X-WP-Nonce': nonce,
            'Content-type': 'application/json'
        }
    }).then(resp => {
        return resp.json()
    }).then(json => {
        memberEdited = json.response.response.success
        newLicense = json.response.response.newLicense
        error = json.response.response.error
    })

    return {
        success: memberEdited,
        data: newLicense,
        error: error
    }
  }

  type LicensesAllocated = {
    title: string,
    product: string,
    expires: string,
    subRows?: LicensesAllocated[]
  }

  const columnHelperLA = createColumnHelper<LicensesAllocated>()

  const columnsLA = [
    columnHelperLA.accessor('title', {
        header: () => <span>License</span>
    }),
    columnHelperLA.accessor('product', {
        header: () => <span>Access from product</span>
    }),
    columnHelperLA.accessor('expires', {
        header: () => <span>Expires</span>
    }),
    columnHelperLA.display({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (propsCell) => {
            return (
                <>
                    {
                        tangibleFields.render({
                            type: 'modal',
                            label: 'Delete',
                            content: 'Are you sure you want to remove this license from the user ?',
                            title: 'Delete ' + 
                            //   props.row.original.username 
                            + ' ?',
                            onValidate: async () => {
                                const result = await removeLicenseMember(propsCell.row.original.id, selectedUser.userId)
                            }
                        }, 'element')
                    }
                </> 
            )
        }
    })
  ]

  async function checkMemberSeat( memberID, seatID ) {
    const nonce = window.group_dashboard.nonce
    const currentRestUrl = window.group_dashboard.base

    let url = ''
    if ( seatID ===  0 ) {
        url = currentRestUrl + 'membersync/licensing/v1/org/' + selectedUser.teamId + '/member/' + memberID + '/assign/seat'
    } else {
        url = currentRestUrl + 'membersync/licensing/v1/org/' + selectedUser.teamId + '/member/' + memberID + '/remove/seat/' + seatID
    }

    let memberAssigned = false

    await fetch(url, {
        method: seatID === 0 ? 'GET' : 'DELETE',
        headers:{
            'X-WP-Nonce': nonce,
            'Content-type': 'application/json'
        }
    }).then(resp => {
        return resp.json()
    }).then(json => {
        memberAssigned = json.success
    })

    return {
        success: memberAssigned
    }
  }

  return (
    <>
      {
        selectedUser && 
        <div className='msync-form msync-form--manage-member'>
          <div className='msync-form__section msync-user-profile'>
            <Profile
              src={ selectedUser.avatarUrl ?? '' }
              size='125px'
              alt={ selectedUser.user && selectedUser.user.trim().length !== 0 ? selectedUser.user : selectedUser.displayName }
              radius={ 'radius-rounded' }
              className={ 'msync-user-profile__image' }
            />
            <div className='msync-user-profile__details'>
            {
              isEditing
              ? <div className='msync-user-profile__details__fullname msync-user-profile__details__fullname--is-editing'>
                  { 
                    response && 
                    <ResponseMessage response={{
                      type: response.type,
                      message: response.message
                    }} />
                  }
                  <div className={`msync-form msync-flex msync-flex--wrap ${ isLoading || isFetching ? 'msync-disabled' : ''}`}>
                    <div className="msync-form__group">
                      <label htmlFor={`manage-user-first-name-${selectedUser?.userId}`}>First Name</label>
                      <input 
                        type="text" 
                        id={`manage-user-first-name-${selectedUser?.userId}`} 
                        placeholder='First Name' 
                        value={ fullName.firstName } 
                        onChange={ (e) => setFullName( prevState => ({ ...prevState, firstName: e.target.value }) ) }
                      />
                    </div>
                    <div className="msync-form__group">
                      <label htmlFor={`manage-user-last-name-${selectedUser?.userId}`}>Last Name</label>
                      <input 
                        type="text" 
                        id={`manage-user-last-name-${selectedUser?.userId}`} 
                        placeholder='Last name' 
                        value={ fullName.lastName } 
                        onChange={ (e) => setFullName( prevState => ({ ...prevState, lastName: e.target.value }) ) } 
                      />
                    </div>
                    <button className='msync-form__submit msync-form__submit--is-style-circle msync-button msync-button--is-style-outline' onClick={ () => handleUpdate() }>
                      <Icons type='check' />
                    </button>
                  </div>
                  
                </div>
              : <div className="msync-user-profile__details__fullname msync-inline-flex msync-flex--density-8">
                  <div className='msync-title msync-title--is-level-2 msync-margin-block-none'>
                    { selectedUser.user && selectedUser.user.trim().length !== 0 ? selectedUser.user : selectedUser.displayName } 
                  </div>
                  <button className='msync-button msync-button--is-size-xs msync-button--is-theme-secondary msync-member-edit-manage-user-btn' onClick={ () => handleEditInfo() }>
                    <Icons type='edit' />
                  </button>
                </div>
              }
              { selectedUser.email ? 
                <div className='msync-user-profile__details__item--email'>{ selectedUser.email }</div>
              : '' }
              <div className='msync-user-profile__details__item--lastseen'>{ selectedUser.lastActivity ? `last seen ${selectedUser.lastActivity}` : 'never seen' }</div>
              
              <div className='msync-inline-flex msync-flex--density-8'>
                  <UserBadge className='msync-margin-block-auto' role={ selectedUser.role } />
                {
                  selectedUser.role === 'manager' && 
                  <MembersTableButton
                    btnName='Demote to member'
                    id={ modalConfirmationMessageId }
                    info={{ ...selectedUser, modal: modalMembersTblBtnId.demoteToMember }}
                    hasModal={ modalId }
                    teamType={selectedUser.teamType}
                  />
                }
                { selectedUser.role === 'member' &&
                  <MembersTableButton
                    btnName='Set as manager'
                    id={ modalConfirmationMessageId }
                    info={{ ...selectedUser, modal: modalMembersTblBtnId.setAsManager }}
                    hasModal={ modalId }
                    teamType={selectedUser.teamType}
                  />
                }
              </div>
            </div>
          </div>
          <div>
          {
            teamData.team_type === 'msynclicenses' && selectedUser.licenses.assigned && <MembersyncTable
            {
                ...{
                    data: selectedUser.licenses.assigned,
                    columns: columnsLA,
                    type: "manageMember"
                }
            }
            />
          }
          <div className='membersync-org-member-allocated-licenses-infos' >
            {
              teamData.team_type === 'msynclicenses' && tangibleFields.render({
                type: 'combo-box',
                label: 'Licenses',
                name: 'membersync-org-member-licenses',
                multiple: true,
                isAsync: true,
                ajaxAction: 'get_licenses',
                asyncArgs: {
                  type: 'license',
                  org: selectedUser.teamId
                }
              })
            }

            {
              teamData.team_type === 'msynclicenses' && tangibleFields.render({
                type: 'combo-box',
                label: 'License Presets',
                name: 'membersync-org-member-license-presets',
                multiple: true,
                isAsync: true,
                ajaxAction: 'get_presets',
                asyncArgs: {
                  type: 'license',
                  org: selectedUser.teamId
                }
              })
            }

            {
              teamData.team_type === 'msynclicenses' && tangibleFields.render({
                name: 'membersync-org-expires-date',
                type: 'date-picker',
                futureOnly: true,
              })
            }
          </div>


          {
            teamData.team_type === 'msynclicenses' && tangibleFields.render({
              type: 'button',
              content: 'Allocate License',
              layout: 'primary',
              onClick: async() => {
                const license = tangibleFields.store.getValue('membersync-org-member-licenses')
                const licensePresets = tangibleFields.store.getValue('membersync-org-member-license-presets')
                const licenseExpiresDate = tangibleFields.store.getValue('membersync-org-expires-date')
                const result = await assignLicenses( license, selectedUser.userId, licensePresets, licenseExpiresDate )
                refetch()
              },
              name: 'membersync-org-allocate-license'
            }, 'element')
          }

          </div>

        </div>
      }
    </>
  )
}

export default UserInfo
